import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { syncChildId } from '../utils/syncChildId';


type VerificationState = 'verifying' | 'success' | 'error';

export default function VerifyMagicLinkPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: authLogin, isAuthenticated, isLoading } = useAuth();
  
  const [state, setState] = useState<VerificationState>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isLoading) return;

    // If already authenticated, redirect
    if (isAuthenticated) {
      navigate('/home', { replace: true });
      return;
    }

    const verifyToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setState('error');
        setErrorMessage('No token provided in the link. Please request a new magic link.');
        return;
      }

      try {
        setState('verifying');
        
        const response = await api.verifyMagicLink({ token });

        // Store auth data
        authLogin(response);

        // Sync childId in localStorage with backend children
        try {
          const children = await api.getChildren();
          syncChildId(children);
        } catch {
          // Non-blocking — childId will be synced on next navigation
        }

        setState('success');
        toast.success('Welcome back!');
        
        // Redirect to home after short delay
        setTimeout(() => {
          navigate('/home', { replace: true });
        }, 1500);
        
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';

        setState('error');

        let displayMessage: string;
        if (errorMsg.includes('expired')) {
          displayMessage = 'This magic link has expired. Please request a new one.';
        } else if (errorMsg.includes('already been used')) {
          displayMessage = 'This magic link has already been used. Please request a new one.';
        } else if (errorMsg.includes('not found')) {
          displayMessage = 'This magic link is invalid. Please request a new one.';
        } else {
          displayMessage = 'Failed to verify magic link.';
        }

        setErrorMessage(displayMessage);
        toast.error(displayMessage);
      }
    };

    verifyToken();
  }, [searchParams, authLogin, navigate, isAuthenticated, isLoading]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-blue-50 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {state === 'verifying' && (
            <>
              <div className="flex justify-center mb-4">
                <svg 
                  className="animate-spin h-12 w-12 text-primary-600" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verifying Your Magic Link
              </h2>
              <p className="text-gray-600">
                Please wait while we log you in...
              </p>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="flex justify-center mb-4">
                <svg 
                  className="h-12 w-12 text-green-500" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Success!
              </h2>
              <p className="text-gray-600">
                You&apos;re now logged in. Redirecting...
              </p>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <svg 
                  className="h-12 w-12 text-red-500" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verification Failed
              </h2>
              <p className="text-gray-600 mb-6">
                {errorMessage}
              </p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
